import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { database } from '../services/firebase';
import { ref, onValue, get } from 'firebase/database';
import { fetchProducts, selectAllProducts } from '../store/slices/productsSlice';
import { Calendar, Clock, FileCode, Gauge, Package, Search, Sparkles, X } from 'lucide-react';
import ExtensionModal from '../components/modals/ExtensionModal';
import { getAuthRedirectPath } from '../utils/authRedirect';
import { isExtensionDiscountEligibleAccess } from '../utils/extensionEligibility';
import { isPurchasableProduct } from '../utils/productRules';

const heroShellClass = 'relative overflow-hidden rounded-3xl border border-primary-100 bg-gradient-to-br from-white via-primary-50 to-amber-50 text-gray-950 shadow-sm dark:border-white/10 dark:bg-gray-950 dark:bg-none dark:text-white dark:shadow-xl';
const heroGlowClass = 'absolute inset-0 bg-gradient-to-br from-primary-500/10 via-accent-500/10 to-amber-400/15 dark:from-primary-500/30 dark:via-accent-500/25 dark:to-amber-400/10';
const heroChipClass = 'mb-2 inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-white/80 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-primary-700 shadow-sm dark:border-white/10 dark:bg-white/[0.08] dark:text-primary-100 dark:shadow-none';
const heroButtonClass = 'inline-flex items-center justify-center gap-2 rounded-2xl border border-primary-100 bg-white/80 px-4 py-2.5 text-sm font-black text-primary-700 shadow-sm transition hover:bg-white dark:border-white/10 dark:bg-white/10 dark:text-white dark:shadow-none dark:hover:bg-white/20';
const heroStatCardClass = 'rounded-2xl border border-primary-100 bg-white/70 px-3 py-2.5 shadow-sm dark:border-white/10 dark:bg-white/[0.08] dark:shadow-none';

const MyScripts = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isAuthenticated, user, loading } = useSelector((state) => state.auth);
  const allProducts = useSelector(selectAllProducts);
  const [productAccess, setProductAccess] = useState([]);
  const [userOrders, setUserOrders] = useState([]);
  const [loginLimit, setLoginLimit] = useState({ count: 0, max: 30 });
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const [extensionModal, setExtensionModal] = useState(null);
  const [scriptSearchQuery, setScriptSearchQuery] = useState('');

  const productById = useMemo(() => {
    return new Map((allProducts || []).map((product) => [String(product.id), product]));
  }, [allProducts]);

  const getAccessTimestamp = useCallback((entry) => {
    const raw = entry?.updatedAt || entry?.grantedAt || entry?.createdAt || 0;
    const parsed = new Date(raw).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  }, []);

  const resolveProduct = useCallback((productId) => productById.get(String(productId || '')) || {}, [productById]);

  const resolveProductName = useCallback((entry) => {
    const product = resolveProduct(entry?.productId);
    return (
      product?.name ||
      product?.Name ||
      entry?.productName ||
      entry?.ProductName ||
      entry?.name ||
      t('my_scripts.product_access', 'Product access')
    );
  }, [resolveProduct, t]);

  useEffect(() => {
    if (allProducts.length === 0) {
      dispatch(fetchProducts());
    }
  }, [allProducts.length, dispatch]);

  useEffect(() => {
    if (!user?.uid) return;
    // Pulse the clock occasionally for remaining days update
    const timer = setInterval(() => setCurrentTime(new Date()), 60000); // every minute

    const userRef = ref(database, `Users/${user.uid}`);
    const unsubscribe = onValue(userRef, async (snapshot) => {
      const data = snapshot.exists() ? snapshot.val() : {};
      const accessNode = data?.ProductAccess || {};
      const ordersNode = data?.Orders || {};
      const orderMap = new Map();
      const legacyOrderIds = [];
      Object.entries(ordersNode).forEach(([orderId, value]) => {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          orderMap.set(orderId, value);
        } else {
          legacyOrderIds.push(orderId);
        }
      });

      if (legacyOrderIds.length > 0) {
        const resolvedOrders = await Promise.all(legacyOrderIds.map(async (orderId) => {
          try {
            const orderSnapshot = await get(ref(database, `Order index/${orderId}`));
            if (!orderSnapshot.exists()) return null;
            const order = orderSnapshot.val();
            return order && order.Customer === user.uid ? { id: orderId, order } : null;
          } catch {
            return null;
          }
        }));
        resolvedOrders.forEach((result) => {
          if (result?.order) orderMap.set(result.id, result.order);
        });
      }

      const ordersList = Array.from(orderMap.entries()).map(([id, order]) => ({ id, ...order }));
      const now = new Date();

      const activeAccess = Object.entries(accessNode)
        .map(([productId, entry]) => {
          const isLifetime = entry.expiresAt === 'lifetime' || entry.expiresAt === null || !entry.expiresAt;
          const isExpired = !isLifetime && entry.expiresAt && new Date(entry.expiresAt) < now;
          
          const normalizedStatus = String(entry.status || 'active').toLowerCase();
          let effectiveStatus = normalizedStatus;
          if (normalizedStatus === 'active' && isExpired) {
            effectiveStatus = 'expired';
          }

          let statusRank = 1;
          if (effectiveStatus === 'active') statusRank = 0;
          else if (effectiveStatus === 'expired') statusRank = 2;

          return {
            productId,
            ...entry,
            effectiveStatus,
            statusRank,
            isExpired,
            isLifetime
          };
        })
        .sort((a, b) => {
          if (a.statusRank !== b.statusRank) return a.statusRank - b.statusRank;
          return getAccessTimestamp(b) - getAccessTimestamp(a);
        });

      setProductAccess(activeAccess);
      setUserOrders(ordersList);
      setLoginLimit({
        count: data?.Login_Count || 0,
        max: data?.Max_login || 30
      });
      setIsLoading(false);
    });

    return () => {
      unsubscribe();
      clearInterval(timer);
    };
  }, [getAccessTimestamp, user?.uid]);

  const activeCount = productAccess.filter((entry) => entry.effectiveStatus === 'active').length;
  const expiredCount = productAccess.filter((entry) => entry.effectiveStatus === 'expired').length;
  const loginMax = Math.max(Number(loginLimit.max) || 1, 1);
  const loginPercent = Math.min(100, Math.round(((Number(loginLimit.count) || 0) / loginMax) * 100));
  const normalizedSearchQuery = scriptSearchQuery.trim().toLowerCase();
  const filteredProductAccess = useMemo(() => {
    if (!normalizedSearchQuery) return productAccess;
    return productAccess.filter((entry) => {
      const product = resolveProduct(entry.productId);
      const searchable = [
        resolveProductName(entry),
        entry.productId,
        entry.effectiveStatus,
        entry.validity,
        product?.category,
        product?.packageName || product?.PackageName || product?.EmbeddedScript?.packageName
      ].filter(Boolean).join(' ').toLowerCase();
      return searchable.includes(normalizedSearchQuery);
    });
  }, [normalizedSearchQuery, productAccess, resolveProduct, resolveProductName]);

  if (!isAuthenticated && !loading) {
    return <Navigate to={getAuthRedirectPath()} replace />;
  }

  if (isLoading) {
    return (
    <div className="min-h-screen bg-gray-50/80 py-4 dark:bg-gray-950 sm:py-6">
      <div className="container mx-auto max-w-5xl px-3 sm:px-4 space-y-3">
          <div className="h-32 animate-pulse rounded-3xl border border-primary-100 bg-primary-50 dark:border-gray-800 dark:bg-gray-900" />
          {[1, 2, 3].map(i => (
            <div key={i} className="h-20 rounded-2xl border border-gray-100 bg-white dark:border-gray-800 dark:bg-gray-900 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/80 py-4 dark:bg-gray-950 sm:py-6">
      <div className="container mx-auto max-w-5xl px-3 sm:px-4">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          <section className={heroShellClass}>
            <div className={heroGlowClass} />
            <div className="relative p-4 sm:p-5">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className={heroChipClass}>
                    <Sparkles className="h-3.5 w-3.5 text-amber-300" />
                    Account Access
                  </div>
                  <h1 className="text-2xl font-black tracking-normal sm:text-3xl">{t('header.my_scripts', 'My Scripts')}</h1>
                  <p className="mt-1 max-w-2xl text-sm font-medium text-gray-600 dark:text-gray-300">
                    {t('my_scripts.manage_access', 'Manage your script licenses and validity period.')}
                  </p>
                </div>
                <a
                  href={`https://t.me/pshsupport?text=${encodeURIComponent(`Hello PSH Support, I would like to request a login limit increase for my account.\n\nEmail: ${user?.email}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={heroButtonClass}
                >
                  <Gauge className="h-4 w-4" />
                  {t('common.increase', 'Request Increase')}
                </a>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-2">
                {[
                  { label: 'Active', value: activeCount, icon: FileCode },
                  { label: 'Expired', value: expiredCount, icon: Clock },
                  { label: t('profile.login_limit'), value: `${loginLimit.count}/${loginLimit.max}`, icon: Gauge },
                ].map((stat) => {
                  const Icon = stat.icon;
                  return (
                    <div key={stat.label} className={heroStatCardClass}>
                      <div className="mb-1 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        <Icon className="h-3.5 w-3.5" />
                        {stat.label}
                      </div>
                      <p className="truncate text-lg font-black text-gray-950 dark:text-white notranslate" translate="no">{stat.value}</p>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 h-1.5 rounded-full bg-primary-100 dark:bg-white/10">
                <div className="h-1.5 rounded-full bg-gradient-to-r from-primary-400 to-accent-400" style={{ width: `${loginPercent}%` }} />
              </div>
            </div>
          </section>

          <div className="rounded-2xl border border-gray-100 bg-white p-2 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              <input
                type="search"
                value={scriptSearchQuery}
                onChange={(event) => setScriptSearchQuery(event.target.value)}
                placeholder={t('my_scripts.search_placeholder', 'Search scripts')}
                className="h-11 w-full rounded-xl border border-transparent bg-gray-50 pl-9 pr-10 text-sm font-semibold text-gray-900 outline-none transition focus:border-primary-200 focus:bg-white focus:ring-2 focus:ring-primary-100 dark:bg-gray-950/60 dark:text-white dark:focus:border-primary-500/30 dark:focus:bg-gray-950 dark:focus:ring-primary-500/10"
              />
              {scriptSearchQuery && (
                <button
                  type="button"
                  onClick={() => setScriptSearchQuery('')}
                  className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/10 dark:hover:text-white"
                  aria-label={t('common.clear', 'Clear')}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div className="space-y-3">
            {productAccess.length === 0 ? (
               <div className="rounded-3xl border border-dashed border-gray-200 bg-white px-4 py-10 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900">
                  <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 text-gray-400 dark:bg-gray-800">
                    <Package className="h-7 w-7" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{t('my_scripts.no_scripts', 'No scripts available')}</h3>
                  <p className="text-gray-500 dark:text-gray-400">{t('profile.no_access')}</p>
               </div>
            ) : filteredProductAccess.length === 0 ? (
               <div className="rounded-3xl border border-dashed border-gray-200 bg-white px-4 py-8 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-100 text-gray-400 dark:bg-gray-800">
                    <Search className="h-6 w-6" />
                  </div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white">{t('my_scripts.no_search_results', 'No matching scripts')}</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('my_scripts.try_different_search', 'Try a different product name or status.')}</p>
               </div>
            ) : (
                filteredProductAccess.map((entry) => {
                  const product = resolveProduct(entry.productId);
                  const productName = resolveProductName(entry);
                  const isActive = entry.effectiveStatus === 'active';
                  const isExpired = entry.effectiveStatus === 'expired';
                  const isLifetime = entry.isLifetime;
                  
                  let remainingDays = 0;
                  if (isActive && !isLifetime && entry.expiresAt) {
                     const expiryDate = new Date(entry.expiresAt);
                     expiryDate.setHours(0,0,0,0);
                     const today = new Date(currentTime);
                     today.setHours(0,0,0,0);
                     remainingDays = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                  }

                  return (
                    <motion.div 
                      key={entry.productId} 
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                      className={`relative overflow-hidden rounded-2xl border bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:bg-gray-900 ${isActive ? 'border-gray-100 dark:border-gray-800' : 'border-red-100 dark:border-red-900/30'}`}
                    >
                      <div className="flex items-center gap-3 p-3 sm:p-3.5">
                          <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${isActive ? 'bg-green-50 text-green-600 dark:bg-green-900/25 dark:text-green-300' : isExpired ? 'bg-amber-50 text-amber-600 dark:bg-amber-900/25 dark:text-amber-300' : 'bg-red-50 text-red-600 dark:bg-red-900/25 dark:text-red-300'}`}>
                            <FileCode className="h-[18px] w-[18px]" />
                          </div>
                          <div className="flex-1 min-w-0">
                              <div className="mb-1.5 flex items-start gap-2">
                                <Link 
                                  to={`/product/${entry.productId}`}
                                  className="truncate text-sm font-black text-gray-900 transition-colors hover:text-primary-600 dark:text-white dark:hover:text-primary-400"
                                  title={productName}
                                >
                                  {productName}
                                </Link>
                              </div>
                              
                              <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">

                                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                  <Calendar size={14} className="text-gray-400" />
                                  <span>{t('my_scripts.expiry', 'Expiry')}:</span>
                                  <span className="font-semibold text-gray-900 dark:text-white">
                                    {isLifetime ? t('orders.lifetime', 'Lifetime') : new Date(entry.expiresAt).toLocaleDateString()}
                                  </span>
                                </div>
                                
                                {!isLifetime && isActive && (
                                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                    <Clock size={14} className={`${remainingDays <= 3 ? 'text-red-500' : 'text-gray-400'}`} />
                                    <span>{t('my_scripts.remaining', 'Remaining')}:</span>
                                    <span className={`font-semibold ${remainingDays <= 3 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                                      {remainingDays > 0 ? `${remainingDays} ${t('orders.days_left', 'Days').replace('{{count}}', '').trim()}` : t('orders.expired', 'Expired')}
                                    </span>
                                  </div>
                                )}
                              </div>
                          </div>

                          <div className="flex w-[86px] shrink-0 flex-col items-end gap-2 self-stretch">
                            <span className={`rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-wider ${
                              isActive 
                                ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800' 
                                : isExpired
                                  ? 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800'
                                  : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800'
                            }`}>
                              {entry.effectiveStatus}
                            </span>
                            {!isLifetime ? (
                              (product ? isPurchasableProduct(product) : false) ? (
                                <button
                                  onClick={() => setExtensionModal({ 
                                    productId: entry.productId, 
                                    basePrice: product.price || 0, 
                                    productName, 
                                    originalOrderId: entry.orderId,
                                    mode: isActive ? 'extend' : 'renew',
                                    currentValidity: entry.validity || '',
                                    remainingDays,
                                    extensionDiscountEligible: isExtensionDiscountEligibleAccess({
                                      productId: entry.productId,
                                      accessRecord: entry,
                                      orders: userOrders
                                    }),
                                    accessSource: entry.source || entry.lastAdjustment?.source || '',
                                    addons: Array.isArray(product?.addons) ? product.addons : [],
                                    addonAccessMap: entry.addons && typeof entry.addons === 'object' ? entry.addons : {}
                                  })}
                                  className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-primary-600 px-2.5 py-1.5 text-xs font-black text-white shadow-sm transition hover:bg-primary-700 active:scale-95 dark:bg-white dark:text-gray-950 dark:hover:bg-gray-200"
                                >
                                  <Calendar size={13} />
                                  <span>{isActive ? t('orders.extend', 'Extend') : t('orders.renew', 'Renew')}</span>
                                </button>
                              ) : (
                                <span className="inline-flex w-full items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 px-2 py-1.5 text-[10px] font-bold text-gray-400 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-500" title="Renewals closed">
                                  {t('product.unavailable', 'Unavailable')}
                                </span>
                              )
                            ) : (
                              <span className="inline-flex w-full items-center justify-center rounded-xl border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-[10px] font-black text-gray-500 dark:border-white/10 dark:bg-white/[0.06] dark:text-gray-400">
                                {t('orders.lifetime', 'Lifetime')}
                              </span>
                            )}
                          </div>
                      </div>
                    </motion.div>
                  );
                })
            )}
          </div>
        </motion.div>
      </div>
      
      <ExtensionModal
        isOpen={!!extensionModal}
        onClose={() => setExtensionModal(null)}
        data={extensionModal}
      />
    </div>
  );
};

export default MyScripts;
